import React from "react";
import {
  Box,
  HStack,
  Text,
  VStack,
  IconButton,
  Stack,
  Icon,
} from "@chakra-ui/react";
import { Tooltip } from "../../ui/tooltip";
import { Guardian, User } from "../../../services/Dtos";
import useIcons from "../../../hooks/useIcons";
import useThemeColors from "../../../hooks/useThemeColors";
import Cad from "../../CustomUi/Cad";
import { useRoleAccess } from "../../../hooks/useRoleAccess";
import { Role } from "../../../services/Enums";

interface GuardianCardProps {
  guardian: Guardian;
  user: User | null;
  editorPolicy: number[];
  accentColor: string;
  onEditGuardian: (guardian: Guardian) => void;
}

const GuardianCard: React.FC<GuardianCardProps> = ({
  guardian,
  onEditGuardian,
}) => {
  const icons = useIcons();
  const colors = useThemeColors();
  const { hasRoleOrHigher } = useRoleAccess();

  return (
    <Cad
      bg={colors.background.card}
      shadow={colors.shadow.card}
      borderRadius="xl"
      hover={true}
    >
      <HStack justify="space-between" align="start" mb={4}>
        <VStack align="start" gap={1}>
          <Text fontWeight="bold" fontSize="md">
            {guardian.guardianTitle} {guardian.guardianFullName}
          </Text>
          <Text fontSize="xs" color={colors.text.secondary} fontWeight="medium">
            GUARDIAN
          </Text>
        </VStack>

        {hasRoleOrHigher(Role.Editor) && (
          <Tooltip
            content="Edit Guardian"
            positioning={{ placement: "left" }}
            showArrow
          >
            <IconButton
              size="sm"
              bg={colors.background.secondary}
              color={colors.text.secondary}
              aria-label="edit guardian"
              _hover={{
                bg: colors.background.tertiary,
                transform: "scale(1.1)",
              }}
              transition="all 0.2s"
              onClick={() => onEditGuardian(guardian)}
            >
              <Icon as={icons.utility.edit} />
            </IconButton>
          </Tooltip>
        )}
      </HStack>

      <Stack gap={3}>
        <HStack gap={3}>
          <Icon
            as={icons.utility.phone}
            color={colors.schoolAccent.icon}
            boxSize={4}
          />
          <Box>
            <Text
              fontSize="xs"
              color={colors.text.secondary}
              fontWeight="medium"
            >
              PHONE
            </Text>
            <Text fontSize="sm" fontWeight="semibold">
              {guardian.guardianPhoneNumber}
            </Text>
          </Box>
        </HStack>

        <HStack gap={3}>
          <Icon
            as={icons.utility.email}
            color={colors.schoolAccent.icon}
            boxSize={4}
          />
          <Box>
            <Text
              fontSize="xs"
              color={colors.text.secondary}
              fontWeight="medium"
            >
              EMAIL
            </Text>
            <Text fontSize="sm" fontWeight="semibold" wordBreak="break-word">
              {guardian.guardianEmail}
            </Text>
          </Box>
        </HStack>

        <HStack gap={3} align="start">
          <Icon
            as={icons.utility.mapPin}
            color={colors.schoolAccent.icon}
            boxSize={4}
            mt={1}
          />
          <Box>
            <Text
              fontSize="xs"
              color={colors.text.secondary}
              fontWeight="medium"
            >
              ADDRESS
            </Text>
            <Text fontSize="sm" fontWeight="semibold" lineHeight="short">
              {guardian.guardianAddress}
            </Text>
          </Box>
        </HStack>
      </Stack>
    </Cad>
  );
};

export default GuardianCard;
